import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useQuery } from '@tanstack/react-query';

/**
 * Canonical `Entity.status` values the backend groups on
 * (ff-api `model/entities.py`, Entity.status String(16)).
 */
export type EntityStatus =
  | 'DRAFT'
  | 'REVIEW'
  | 'REJECTED'
  | 'LIVE'
  | 'INACTIVE'
  | 'SUSPENDED'
  | 'ARCHIVED'
  | 'DELETED';

export type ListingStatsPayload = {
  total: number;
  by_status: Partial<Record<EntityStatus, number>>;
  top_categories: { etid: number; name: string; count: number }[] | null;
  created_last_7d: number;
  created_last_30d: number;
  scope_etid: number | null;
};

/**
 * `GET /api/admin/listings/analytics/summary`
 *
 * Returns a bare `jsonify` dict, NOT the `{status, data}` envelope — but the
 * error path in ff-api does nest under `data`, so unwrap defensively (same
 * trap that silently emptied the listings table).
 *
 * Backend caveats worth knowing before trusting these numbers:
 *  - `total` does NOT exclude soft-deleted entities, so it can exceed the sum
 *    of the statuses you'd consider live. We show status counts, not `total`.
 *  - `top_categories` is null whenever `etid` is supplied.
 */
const unwrap = (response: any): ListingStatsPayload => {
  const payload = (response?.data ?? response) ?? {};

  return {
    total: payload.total ?? 0,
    by_status: payload.by_status ?? {},
    top_categories: payload.top_categories ?? null,
    created_last_7d: payload.created_last_7d ?? 0,
    created_last_30d: payload.created_last_30d ?? 0,
    scope_etid: payload.scope_etid ?? null,
  };
};

/** `live` (server default) hides tester=1 listings, `test` shows only them, `all` both. */
export type TestScope = 'live' | 'test' | 'all';

export const listingStatsKey = (etid?: number, test: TestScope = 'live') =>
  ['listings', 'stats', etid ?? 'all', test] as const;

const useListingStatsFetcher = () => {
  const request = useClientStore((state) => state.request);

  return withErrorHandling(async (etid?: number, test: TestScope = 'live'): Promise<ListingStatsPayload> => {
    const qs = new URLSearchParams({ test });
    if (etid) qs.set('etid', String(etid));
    const url = `/api/admin/listings/analytics/summary?${qs}`;

    return unwrap(await request(url, 'GET'));
  }, 'An unexpected error occurred while fetching listing stats');
};

export const useListingStatsQuery = (etid?: number, test: TestScope = 'live') => {
  const fetchStats = useListingStatsFetcher();

  return useQuery({
    queryKey: listingStatsKey(etid, test),
    queryFn: () => fetchStats(etid, test),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    // Don't retry auth errors — a 401/403 on this admin-analytics endpoint is a
    // permission denial, not an expired session. Retrying stacks 401s and trips
    // the client store's forced-logout burst check, redirecting to sign-in.
    retry: (failureCount, error: any) =>
      error?.status === 401 || error?.status === 403 ? false : failureCount < 1,
  });
};
